import { useState } from "react";

/* Airline logo served from OUR OWN domain (`/api/airline-logo/{IATA}`), with a
   graceful monogram fallback (the IATA code in a teal-tinted circle) when the
   image 404s or the code is missing.

   We deliberately DO NOT use third-party hosts like pics.avs.io: those are
   commonly stripped by ad/tracker blockers (the path contains "pics"/"avs"),
   so the logo silently vanished for many users. A same-origin URL is never
   blocked. Square, rounded. */

interface AirlineLogoProps {
  /** IATA airline code, e.g. "AA". */
  iata: string | null | undefined;
  /** Backend-supplied same-origin url (`/api/airline-logo/{IATA}`); preferred. */
  logoUrl?: string | null;
  /** Pixel size of the square logo. */
  size?: number;
  className?: string;
}

function Monogram({ iata, size }: { iata: string | null | undefined; size: number }) {
  const text = (iata || "??").slice(0, 2).toUpperCase();
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-md bg-[hsl(var(--primary))]/[0.12] font-mono font-bold text-[hsl(var(--accent-foreground))]"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      aria-hidden="true"
    >
      {text}
    </span>
  );
}

/** Build the same-origin logo endpoint for an IATA code. */
export function airlineLogoUrl(iata: string | null | undefined): string | null {
  const code = iata ? iata.trim().toUpperCase() : "";
  if (!code) return null;
  return `/api/airline-logo/${encodeURIComponent(code)}`;
}

export default function AirlineLogo({ iata, logoUrl, size = 36, className }: AirlineLogoProps) {
  const [failed, setFailed] = useState(false);
  const code = iata ? iata.trim().toUpperCase() : "";

  // Prefer the backend-supplied same-origin url; otherwise derive it from the
  // IATA code. Both load from our domain, so ad-blockers leave them alone.
  const src = (logoUrl && logoUrl.trim()) || airlineLogoUrl(code);

  if (!src || failed) {
    return <Monogram iata={code} size={size} />;
  }

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
      className={
        "shrink-0 rounded-md object-contain" + (className ? " " + className : "")
      }
      style={{ width: size, height: size }}
    />
  );
}
